import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { capture } from "../lib/analytics";
import { PrimaryButton, QuietButton, Field, ErrorText, Muted, GildedFrame } from "./ui";

/** Email + 6-digit code. Identical model to the apps: no password, no OAuth. */
export function SignIn({ reason, onDone }: { reason?: string; onDone?: () => void }) {
  const { signIn } = useAuth();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const msg = (e: unknown) =>
    e instanceof ApiError ? e.message : "Something went wrong. Please try again.";

  async function send() {
    setBusy(true);
    setErr(null);
    try {
      await api.requestCode(email.trim());
      capture("webEmailCaptured");
      setStep("code");
    } catch (e) {
      setErr(msg(e));
    }
    setBusy(false);
  }

  async function verify() {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.verifyCode(email.trim(), code);
      signIn(res.token, res.email, res.userId);
      capture("webSignedIn", { isNew: res.isNew });
      onDone?.();
    } catch (e) {
      setErr(msg(e));
    }
    setBusy(false);
  }

  return (
    <GildedFrame className="mx-auto max-w-md">
      <div className="p-5">
        <h2 className="font-serif text-2xl text-ivory">
          {step === "email" ? "Sign in" : "Enter your code"}
        </h2>
        <Muted className="mt-1">
          {step === "email"
            ? reason || "Your credits and portraits follow your account, on any device."
            : `We sent a 6-digit code to ${email}.`}
        </Muted>

        <div className="mt-4 space-y-3">
          {step === "email" ? (
            <>
              <Field
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && email && void send()}
              />
              <PrimaryButton onClick={send} busy={busy} busyLabel="Sending…" disabled={!email}>
                Email me a code
              </PrimaryButton>
            </>
          ) : (
            <>
              <Field
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && code.length === 6 && void verify()}
              />
              <PrimaryButton onClick={verify} busy={busy} busyLabel="Signing in…" disabled={code.length < 6}>
                Sign in
              </PrimaryButton>
              <div className="flex justify-center">
                <QuietButton
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setErr(null);
                  }}
                >
                  Use a different email
                </QuietButton>
              </div>
            </>
          )}
          <ErrorText>{err}</ErrorText>
          <Muted className="pt-1 text-center text-[11px]">
            We only use your email to sign you in and send receipts.
          </Muted>
        </div>
      </div>
    </GildedFrame>
  );
}
